import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Globe, X } from 'lucide-react';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';
import { FLOATING_KEYBOARD_ANIM_MS, useModalBackdropFade } from '../../utils/learnerSlideModalMotion';
import { localeMirroredNavArrowClass } from '../../utils/localeNavArrow';
import { ELEVATED_HINT_Z_INDEX } from '../../utils/anchoredHintPanel';
import type { AnchorRect } from '../../hooks/useNonFarsiContentGate';
import { isNonFarsiContentHintSuppressed } from '../../utils/nonFarsiContentHint';
import { useHistoryBackClose } from '../../hooks/useHistoryBackClose';

const PANEL_ANIM_MS = FLOATING_KEYBOARD_ANIM_MS;

export interface NonFarsiContentHintModalProps {
  open: boolean;
  isRtl: boolean;
  anchorRect?: AnchorRect | null;
  contentKey?: string;
  onDismiss: () => void;
  onContinue: (dontShowAgain: boolean) => void;
}

/**
 * Hint shown after learners open English-primary lesson/quiz/exercise content while UI locale is not English.
 * Portaled above other modals (Keep Learning, quiz/exercise modals), positioned under the app header.
 */
export const NonFarsiContentHintModal: React.FC<NonFarsiContentHintModalProps> = ({
  open,
  isRtl,
  onDismiss,
  onContinue,
}) => {
  const { t } = useTranslation('learning');
  const [mounted, setMounted] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingActionRef = useRef<'dismiss' | 'continue' | null>(null);
  const dontShowAgainRef = useRef(false);

  useEffect(() => {
    if (open) {
      const suppressed = isNonFarsiContentHintSuppressed();
      setMounted(true);
      setExiting(false);
      setDontShowAgain(suppressed);
      dontShowAgainRef.current = suppressed;
      pendingActionRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  const finishExit = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setMounted(false);
    setExiting(false);

    if (action === 'continue') {
      onContinue(dontShowAgainRef.current);
    } else {
      onDismiss();
    }
  }, [onContinue, onDismiss]);

  const startExit = useCallback(
    (action: 'dismiss' | 'continue') => {
      if (exiting) return;
      pendingActionRef.current = action;
      if (prefersReducedMotion()) {
        finishExit();
        return;
      }
      setExiting(true);
      exitTimerRef.current = setTimeout(finishExit, PANEL_ANIM_MS);
    },
    [exiting, finishExit]
  );

  useHistoryBackClose(open, () => startExit('dismiss'), 'non-farsi-content-hint');

  const backdropVisible = useModalBackdropFade(mounted, exiting);

  if (!mounted) return null;

  const slideClass = exiting
    ? isRtl
      ? 'lesson-not-passed-hint-exit-rtl'
      : 'lesson-not-passed-hint-exit-ltr'
    : isRtl
      ? 'lesson-not-passed-hint-enter-rtl'
      : 'lesson-not-passed-hint-enter-ltr';

  const panel = (
    <>
      <div
        className={`keyboard-slide-modal-backdrop fixed inset-0 bg-black/25 dark:bg-black/40 pointer-events-auto${
          backdropVisible ? ' is-visible' : ''
        }`}
        style={{ zIndex: ELEVATED_HINT_Z_INDEX - 1 }}
        onClick={() => startExit('dismiss')}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none fixed inset-x-0 top-[4.5rem] flex justify-center px-3 sm:top-20"
        style={{ zIndex: ELEVATED_HINT_Z_INDEX }}
        role="presentation"
      >
        <div
          className={`lesson-not-passed-hint-panel pointer-events-auto w-full max-w-[22rem] overflow-hidden rounded-xl border border-sky-200/80 bg-white/95 shadow-lg shadow-sky-900/10 backdrop-blur-sm dark:border-sky-800/45 dark:bg-gray-900/95 dark:shadow-black/30 ${slideClass}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="non-farsi-content-hint-title"
          aria-describedby="non-farsi-content-hint-desc"
        >
          <div className="relative px-3.5 pb-3 pt-3 sm:px-4 sm:pb-3.5 sm:pt-3.5">
            <button
              type="button"
              onClick={() => startExit('dismiss')}
              className="absolute end-2 top-2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              aria-label={t('nonFarsiContent.dismiss')}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>

            <div className="flex gap-2.5 pe-6">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-200">
                <Globe className="h-4 w-4" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p
                  id="non-farsi-content-hint-title"
                  className="text-sm font-semibold leading-snug text-gray-900 dark:text-gray-50"
                >
                  {t('nonFarsiContent.title')}
                </p>
                <p
                  id="non-farsi-content-hint-desc"
                  className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300"
                  dir="rtl"
                >
                  {t('nonFarsiContent.message')}
                </p>
              </div>
            </div>

            <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg px-0.5 py-0.5 text-xs text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setDontShowAgain(checked);
                  dontShowAgainRef.current = checked;
                }}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800"
              />
              <span dir="rtl">{t('nonFarsiContent.dontShowAgain')}</span>
            </label>

            <div className="mt-3 flex flex-col gap-1.5 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  dontShowAgainRef.current = dontShowAgain;
                  startExit('continue');
                }}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                {t('nonFarsiContent.continue')}
                <ArrowRight
                  className={localeMirroredNavArrowClass(isRtl, 'h-3.5 w-3.5 shrink-0')}
                  aria-hidden
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return typeof document !== 'undefined'
    ? createPortal(panel, document.body)
    : panel;
};
